import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import { useIntl, FormattedMessage } from 'react-intl';

import {
  Heading, Section, Container, FSCCertificate,
} from '@printdeal/ui-library';

import { PaymentMethodIconGrid } from '@printdeal/checkout-frontend';
import FooterLinkSection from './FooterLinkSection';

import { getConfiguration } from '../../../helpers/Configuration';

interface NodeProps {
  locale?: string;
  title?: string;
  items?: ItemProps[];
}

interface FooterQueryData {
  navItems: {
    nodes: NodeProps[];
  };
}

interface ItemProps {
  title?: string;
  icon?: string;
  url?: string;
  contentful_id?: string;
  openInNewTab?: boolean;
  children?: {
    path?: string;
  };
}

const ExtendedFooterContainer = () => {
  const intl = useIntl();
  const { locale } = intl;
  const siteConfigForBrand = getConfiguration();
  const footerQuery = graphql`
  fragment navItem on ContentfulNavItem {
    title
    icon
    url
    contentful_id
    openInNewTab
    children: category {
      typename: __typename
      ... on Node {
        ... on ContentfulPageContainer {
          path
          title
        }
        ... on ContentfulSiteCategory {
          path
          title
        }
        ... on ContentfulProductContent {
          path
          title
        }
      }
    }
  }
  query {
    navItems: allContentfulNavigation(filter: {type: {eq: "Extended Footer"}}) {
      nodes {
        title
        contentful_id
        locale: node_locale
        items: contentfulchildren {
          ... on ContentfulNavItem {
            ...navItem
            items: contentfulchildren {
              ...navItem
            }
          }
        }
      }
    }
  }
`;

  const {
    navItems: { nodes },
  } = useStaticQuery<FooterQueryData>(footerQuery);

  const nodesForLocale = nodes.filter((item: NodeProps) => item.locale === locale);

  if (nodesForLocale.length === 0) return null;

  const { items } = nodesForLocale[0];

  if (!items?.length) return null;

  return (
    <Section className="pd-text-sm">
      <Container data-cy="extended-footer-navs" className="pd-grid pd-gap-6 sm:pd-grid-cols-2 md:pd-grid-cols-4">
        {
          items.map((section: ItemProps) => {
            const { title, contentful_id: contentfulId } = section;
            return (
              <nav data-cy="extended-footer-nav" key={`${section}-${contentfulId}`} aria-labelledby={`website-footer-heading-${contentfulId}`}>
                <Heading as="div" size="3" className="pd-mb-4" id={`website-footer-heading-${contentfulId}`}>{title}</Heading>
                <FooterLinkSection section={section} />
              </nav>
            );
          })
        }
        <div className="pd-mt-6 pd-flex pd-flex-col md:pd-mt-1 md:pd-gap-2">
          <PaymentMethodIconGrid />
          {siteConfigForBrand.fsc.enabled && (
            <div className="pd-m-auto pd-mt-6 pd-flex pd-gap-2 xl:pd-ml-5">
              <FSCCertificate licenseCode={siteConfigForBrand.fsc.licenseCode} />
              <div className="pd-w-64 pd-text-wrap pd-break-words pd-text-xs pd-text-dim-800 md:pd-w-24 lg:pd-w-40 xl:pd-w-48">
                <FormattedMessage
                  id="footer.fsc.text"
                  defaultMessage="
                  Drukwerkdeal is an FSC certified company
                  with license code {licenseCode} and certificate
                  code {certificateCode}. Only products labeled as such are FSC certified"
                  values={{
                    licenseCode: siteConfigForBrand.fsc.licenseCode,
                    certificateCode: siteConfigForBrand.fsc.certificateCode,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </Container>
    </Section>
  );
};

export default ExtendedFooterContainer;
